'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { FlightClass, Gender } from '@prisma/client';
import { IconMail } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';
import FormInput from '@/components/forms/form-input';
import { Form } from '@/components/ui/form';
import { flightBookingSchema } from '@/schemas';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';
import { usePageViewTracking } from '@/utils/hooks';

type FormData = z.infer<typeof flightBookingSchema>;

export default function FlightTicketForm() {
  // Track page view
  usePageViewTracking();

  const form = useForm<FormData>({
    resolver: zodResolver(flightBookingSchema),
    defaultValues: {
      fullName: '',
      gender: 'Male',
      nationality: '',
      email: '',
      phone: '',
      message: '',
      source: '',
      destination: '',
      departureDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      returnDate: new Date(new Date().setDate(new Date().getDate() + 14)),
      passengers: 1,
      class: 'Economy',
    },
  });

  const bookMutation = api.flightBookings.book.useMutation({
    onSuccess: () => {
      // Track successful submission
      trackEvent({
        action: 'flight_booking_form_submit_success',
        category: 'forms',
        label: 'Flight Booking Form Success',
        value: 1,
      });

      toast.success(
        'Thank you for your flight booking request! We will get back to you soon.',
      );
      form.reset();
    },
    onError: (error) => {
      // Track submission error
      trackEvent({
        action: 'flight_booking_form_submit_error',
        category: 'forms',
        label: error.message || 'Unknown error',
        value: 1,
      });

      toast.error(error.message || 'Something went wrong. Please try again.');
    },
  });

  // Track form interaction
  const hasInteracted = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!hasInteracted.current && name && value) {
        hasInteracted.current = true;

        trackEvent({
          action: 'flight_booking_form_started',
          category: 'forms',
          label: 'Flight Booking Form Interaction',
          value: 1,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (data: FormData) => {
    // Track submission attempt
    trackEvent({
      action: 'flight_booking_form_submit',
      category: 'forms',
      label: 'Flight Booking Form',
      value: 1,
    });

    bookMutation.mutate(data);
  };

  // We don't need custom middleware functions anymore as they're handled automatically by FormInput

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormInput
          type="text"
          id="fullName"
          label="Full Name"
          placeholder="Your full name"
          form={form}
        />

        <FormInput
          type="select"
          id="gender"
          label="Gender"
          required
          options={Object.values(Gender).map((gender) => ({
            value: gender,
            label: gender,
          }))}
          form={form}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            type="text"
            id="nationality"
            label="Nationality"
            placeholder="Your nationality"
            form={form}
          />

          <FormInput
            id="email"
            label="Email"
            type="email"
            required
            placeholder="your.email@example.com"
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="phone"
            label="Phone"
            type="tel"
            required
            placeholder="Your phone number"
            form={form}
          />

          <FormInput
            id="passengers"
            label="Number of Passengers"
            type="number"
            required
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="source"
            label="Departure From"
            type="text"
            required
            placeholder="City or airport"
            form={form}
          />

          <FormInput
            id="destination"
            label="Arrival To"
            type="text"
            required
            placeholder="City or airport"
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="departureDate"
            label="Departure Date"
            type="date"
            required
            form={form}
          />

          <FormInput
            id="returnDate"
            label="Return Date"
            type="date"
            required
            form={form}
          />
        </div>

        <FormInput
          id="class"
          label="Travel Class"
          type="select"
          required
          options={Object.values(FlightClass).map((flightClass) => ({
            value: flightClass,
            label: flightClass.replace(/([A-Z])/g, ' $1').trim(),
          }))}
          form={form}
        />

        <FormInput
          id="message"
          label="Message"
          type="textarea"
          required
          placeholder="Additional information about your flight booking"
          rows={5}
          form={form}
        />

        {bookMutation.isSuccess && (
          <div className="flex flex-col justify-center items-center gap-2 p-4 bg-green-100 rounded-lg text-green-800">
            <IconMail className="text-green-800" size={90} />
            <p className="text-lg">
              You have successfully submitted your flight booking request. Please
              check your email for verification. You can also check your spam folder.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={bookMutation.isPending}
          className="bg-accent text-black font-medium rounded-lg w-fit px-12 py-3 hover:bg-accent/90 transition-colors disabled:bg-blue-400"
        >
          {bookMutation.isPending ? 'Submitting...' : 'Book Flight'}
        </button>
      </form>
    </Form>
  );
}
